import React, { createContext, useContext, useState, useCallback } from 'react';

interface PanelInfo {
  id: string;
  level: number;
  title?: string;
}

interface SidePanelContextType {
  panels: PanelInfo[];
  registerPanel: (id: string, title?: string) => number;
  unregisterPanel: (id: string) => void;
  closeAll: () => void;
  getPanelLevel: (id: string) => number;
  getTotalPanels: () => number;
}

const SidePanelContext = createContext<SidePanelContextType | undefined>(undefined);

export const SidePanelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [panels, setPanels] = useState<PanelInfo[]>([]);

  const registerPanel = useCallback((id: string, title?: string) => {
    let level = 0;
    setPanels((prev) => {
      // Check if panel already exists
      const existing = prev.find(p => p.id === id);
      if (existing) {
        return prev;
      }
      // Calculate level for new panel
      level = prev.length;
      return [...prev, { id, level, title }];
    });
    return level;
  }, []);

  const unregisterPanel = useCallback((id: string) => {
    setPanels((prev) => {
      const filtered = prev.filter(p => p.id !== id);
      // Recalculate levels
      return filtered.map((panel, index) => ({ ...panel, level: index }));
    });
  }, []);

  const closeAll = useCallback(() => {
    setPanels([]);
  }, []);

  const getPanelLevel = useCallback((id: string) => {
    const panel = panels.find(p => p.id === id);
    return panel ? panel.level : 0;
  }, [panels]);

  const getTotalPanels = useCallback(() => {
    return panels.length;
  }, [panels]);

  return (
    <SidePanelContext.Provider
      value={{
        panels,
        registerPanel,
        unregisterPanel,
        closeAll,
        getPanelLevel,
        getTotalPanels,
      }}
    >
      {children}
    </SidePanelContext.Provider>
  );
};

export const useSidePanelContext = () => {
  const context = useContext(SidePanelContext);
  if (!context) {
    throw new Error('useSidePanelContext must be used within SidePanelProvider');
  }
  return context;
};

